/**
 * =====================================================================
 * apps/web/src/lib/stripe/payment-errors.ts
 * =====================================================================
 * FUENTE ÚNICA de mensajes de error de pago PARA EL USUARIO.
 *
 * Principios:
 *   1. Lenguaje de usuario final: sin jerga técnica ("webhooks",
 *      "VITE_*", "401", "SetupIntent", "JS exception"…).
 *   2. Siempre accionable: qué pasó + qué puede hacer el usuario +
 *      canal de contacto (info@brickshare.org).
 *   3. El mismo código → el mismo mensaje en todas las superficies
 *      (CardSetup, Identity, Membership, brickpoints, borrowing).
 *
 * Estos mensajes se acompañan de telemetría (lib/stripe/telemetry.ts)
 * para poder diagnosticar el abandono en Sentry/GA4.
 * =====================================================================
 */

export const SUPPORT_CONTACT = "info@brickshare.org";

export type PaymentErrorCode =
  /** stripe.js o el PaymentElement no cargan (red, adblock, protección estricta, navegador). */
  | "stripe_js_load"
  /** No se pudo preparar el guardado de la tarjeta (fallo al crear el SetupIntent). */
  | "setup_init_error"
  /** Excepción lanzada por stripe.js en elements.submit / confirmSetup / handleNextAction. */
  | "confirm_exception"
  /** Watchdog de confirmación (3DS/SCA) agotado sin resolver. */
  | "confirm_timeout"
  /** Tarjeta rechazada por el banco (decline code de Stripe). */
  | "card_declined"
  /** Tarjeta caducada. */
  | "expired_card"
  /** SetupIntent OK pero la confirmación (webhook) no llegó en el tiempo de polling. */
  | "setup_confirm_timeout"
  /** Checkout OK pero la confirmación (webhook) no llegó en el tiempo de polling. */
  | "membership_confirm_timeout"
  /** Error técnico en stripe.verifyIdentity (no es cancelación del usuario). */
  | "identity_verify_error"
  /** Watchdog de verificación: la ventana de Stripe no se abrió. */
  | "identity_verify_timeout"
  /** JWT expirado (401 en Edge Function, habitual en móvil con pantalla bloqueada). */
  | "session_expired"
  /** Error genérico de pago (fallback). */
  | "generic";

const MESSAGES: Record<PaymentErrorCode, string> = {
  stripe_js_load:
    "No se pudo cargar el formulario de pago seguro. Comprueba tu conexión a internet y, si " +
    "tienes un bloqueador de anuncios o un filtro de privacidad activo (p. ej. la «protección " +
    "estricta» del navegador), desactívalo para brickshare.org e inténtalo de nuevo. Si el " +
    `problema persiste, prueba con otro navegador (Chrome, Safari o Firefox) o dispositivo, o ` +
    `contáctanos en ${SUPPORT_CONTACT}.`,
  setup_init_error:
    "No se pudo preparar el guardado de tu tarjeta. Revisa tu conexión a internet e " +
    `inténtalo de nuevo; si el problema persiste, contáctanos en ${SUPPORT_CONTACT}.`,
  confirm_exception:
    "Se ha producido un error al procesar tu tarjeta. No se ha realizado ningún cargo. " +
    `Puedes intentarlo de nuevo; si el problema persiste, contáctanos en ${SUPPORT_CONTACT}.`,
  confirm_timeout:
    "La validación de la tarjeta está tardando más de lo habitual. Puede que tu banco aún " +
    "esté procesando la verificación: si no te ha llegado ningún cargo, espera unos minutos " +
    `e inténtalo de nuevo. Si el problema persiste, contáctanos en ${SUPPORT_CONTACT}.`,
  card_declined:
    "Tu banco ha rechazado la tarjeta. Inténtalo de nuevo con la misma tarjeta o con otra " +
    `tarjeta de crédito. Si el problema persiste, consulta con tu banco o contáctanos en ${SUPPORT_CONTACT}.`,
  expired_card:
    `Tu tarjeta está caducada. Actualízala en tu perfil e inténtalo de nuevo. Si tienes problemas, contáctanos en ${SUPPORT_CONTACT}.`,
  setup_confirm_timeout:
    "Hemos validado tu tarjeta, pero no hemos podido confirmar su guardado en el tiempo " +
    "previsto. No se ha realizado ningún cargo. Puedes intentarlo de nuevo; si el problema " +
    `persiste, contáctanos en ${SUPPORT_CONTACT}.`,
  membership_confirm_timeout:
    "No hemos podido confirmar tu pago en el tiempo previsto. Si has sido cargado, tu " +
    "membresía se activará en unos minutos y podrás verificarlo en tu panel. Puedes " +
    `reintentar o contáctanos en ${SUPPORT_CONTACT}.`,
  identity_verify_error:
    "La verificación de identidad no se ha completado por un problema técnico. Suele pasar " +
    "cuando el navegador o un filtro de privacidad bloquea la ventana de verificación de " +
    "Stripe (revisa el «bloqueador de anuncios» o la «protección estricta» cross-site). " +
    "Puedes intentarlo de nuevo; si el problema persiste, prueba con otro navegador (Chrome, " +
    `Safari o Firefox) o dispositivo, o contáctanos en ${SUPPORT_CONTACT}.`,
  identity_verify_timeout:
    "La ventana de verificación no se ha abierto en el tiempo previsto. Comprueba tu " +
    "conexión y los filtros de privacidad del navegador, e inténtalo de nuevo. Si el " +
    `problema persiste, prueba con otro navegador o dispositivo, o contáctanos en ${SUPPORT_CONTACT}.`,
  session_expired:
    `Tu sesión ha caducado por seguridad. Identifícate de nuevo para continuar donde lo habías dejado. Si el problema persiste, contáctanos en ${SUPPORT_CONTACT}.`,
  generic:
    "No se ha podido procesar el pago. No se ha realizado ningún cargo. Puedes intentarlo " +
    `de nuevo; si el problema persiste, contáctanos en ${SUPPORT_CONTACT}.`,
};

/**
 * Mensaje de usuario para un código de error de pago.
 * @param code Código del error (ver PaymentErrorCode).
 * @param detail Detalle opcional accionable (p. ej. motivo corto del banco). Se añade
 *               entre paréntesis al final del mensaje estándar.
 */
export function paymentErrorMessage(code: PaymentErrorCode, detail?: string): string {
  const base = MESSAGES[code] ?? MESSAGES.generic;
  const trimmed = detail?.trim();
  return trimmed ? `${base} (${trimmed})` : base;
}

/**
 * ¿El mensaje propio (de Stripe o de un error) es apto para mostrarlo al
 * usuario? Stripe entrega mensajes localizados (es) en el Payment Element;
 * los usamos si son cortos y legibles. Si no, se usa el mapa estándar.
 */
export function isUsableErrorMessage(message: unknown): boolean {
  if (typeof message !== "string") return false;
  const t = message.trim();
  return t.length > 0 && t.length <= 300 && !/undefined|null|error 5|exception/i.test(t);
}

interface MaybeHttpError {
  status?: number;
  message?: string;
}

interface MaybeData {
  success?: boolean;
  error?: string;
}

/**
 * Clasifica el resultado de `supabase.functions.invoke(...)` en un mensaje de USUARIO.
 *
 * Orden de preferencia:
 *   1. El servidor ya devolvió un mensaje accionable en `data.error`
 *      (los edge functions devuelven mensajes en lenguaje de usuario).
 *   2. 401 / "Unauthorized" → sesión caducada (no mostrar el mensaje crudo en inglés).
 *   3. Fallback genérico.
 *
 * @param error  `error` devuelto por `functions.invoke` (puede ser FunctionsHttpError).
 * @param data   `data` devuelto por `functions.invoke` (puede contener `error` de negocio).
 */
export function classifySupabaseFunctionError(error: unknown, data?: unknown): string {
  const d = (data ?? {}) as MaybeData | null;
  if (d && typeof d === "object" && typeof d.error === "string" && d.error.trim() !== "") {
    return d.error;
  }
  const e = (error ?? {}) as MaybeHttpError | null;
  const msg = typeof e?.message === "string" ? e.message : "";
  const isUnauthorized =
    e?.status === 401 || /unauthorized|invalid token|missing header/i.test(msg);
  if (isUnauthorized) {
    return MESSAGES.session_expired;
  }
  return MESSAGES.generic;
}

/**
 * ¿El error de Stripe (de confirmSetup/confirmPayment) es una tarjeta rechazada?
 * Mapea `code`/`decline_code` habituales a `card_declined` para el mapa de mensajes.
 */
export function mapStripeConfirmError(error: {
  code?: string;
  decline_code?: string;
  message?: string;
} | null | undefined): PaymentErrorCode {
  if (!error) return "generic";
  if (error.code === "expired_card" || error.decline_code === "expired_card") {
    return "expired_card";
  }
  if (
    error.code === "card_declined" ||
    error.code === "card_error" ||
    error.code === "insufficient_funds" ||
    error.decline_code === "insufficient_funds" ||
    error.decline_code === "card_declined" ||
    error.decline_code === "lost_card" ||
    error.decline_code === "stolen_card" ||
    error.decline_code === "pickup_card" ||
    error.decline_code === "expired_card"
  ) {
    return "card_declined";
  }
  return "generic";
}
